import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Users, FileWarning, Filter, Loader2 } from "lucide-react";

const STATUS_LABELS = {
  alta: "Alta", baja_medica: "Baja medica", vacaciones: "Vacaciones",
  excedencia: "Excedencia", baja_definitiva: "Baja definitiva",
};

export default function ExportPage() {
  const { axiosAuth, user } = useAuth();
  const [exportType, setExportType] = useState("absences");
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(null);
  const [countLoading, setCountLoading] = useState(false);

  // Catalogs
  const [departments, setDepartments] = useState([]);
  const [centers, setCenters] = useState([]);
  const [positions, setPositions] = useState([]);
  const [profCategories, setProfCategories] = useState([]);
  const [contribGroups, setContribGroups] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [requestTypes, setRequestTypes] = useState([]);
  const [leaveStates, setLeaveStates] = useState([]);

  // Filters
  const [filters, setFilters] = useState({
    month: "", date_from: "", date_to: "",
    center_id: "", department_id: "", employee_id: "", manager_id: "",
    request_type: "", status: "", doc_status: "",
    employment_status: "", position_id: "", professional_category_id: "",
    agreement_id: "", cotization_group_id: "", cost_center_id: "",
  });

  const loadCatalogs = useCallback(async () => {
    try {
      const [dRes, cRes, pRes, pcRes, cgRes, agrRes, ccRes, empRes, cfgRes] = await Promise.all([
        axiosAuth().get("/departments"),
        axiosAuth().get("/centers"),
        axiosAuth().get("/positions"),
        axiosAuth().get("/professional-categories"),
        axiosAuth().get("/contribution-groups"),
        axiosAuth().get("/agreements"),
        axiosAuth().get("/cost-centers"),
        axiosAuth().get("/employees"),
        axiosAuth().get("/hr/config"),
      ]);
      setDepartments(dRes.data);
      setCenters(cRes.data);
      setPositions(pRes.data);
      setProfCategories(pcRes.data);
      setContribGroups(cgRes.data);
      setAgreements(agrRes.data);
      setCostCenters(ccRes.data);
      setEmployees(empRes.data);
      setRequestTypes(cfgRes.data?.request_types || []);
      setLeaveStates(cfgRes.data?.leave_states || []);
    } catch {}
  }, [axiosAuth]);

  useEffect(() => { loadCatalogs(); }, [loadCatalogs]);

  const f = (field, value) => setFilters(prev => ({ ...prev, [field]: value }));

  // Build params from non-empty filters
  const buildParams = useCallback(() => {
    const params = { export_type: exportType };
    Object.entries(filters).forEach(([k, v]) => {
      if (v && v !== "_none") params[k] = v;
    });
    return params;
  }, [filters, exportType]);

  // Count preview
  const refreshCount = useCallback(async () => {
    setCountLoading(true);
    try {
      const res = await axiosAuth().get("/exports/count", { params: buildParams() });
      setCount(res.data.count);
    } catch { setCount(null); }
    finally { setCountLoading(false); }
  }, [axiosAuth, buildParams]);

  useEffect(() => {
    const timer = setTimeout(refreshCount, 300);
    return () => clearTimeout(timer);
  }, [refreshCount]);

  const doExport = async () => {
    if (count === 0) { toast.error("No hay registros para exportar con los filtros seleccionados"); return; }
    setLoading(true);
    try {
      const endpoint = `/exports/${exportType}`;
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v && v !== "_none") params[k] = v;
      });
      const res = await axiosAuth().get(endpoint, { params, responseType: "blob" });
      const disposition = res.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `rrhh_export_${exportType}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exportacion generada: ${filename}`);
    } catch (e) { toast.error("Error al generar la exportacion"); }
    finally { setLoading(false); }
  };

  const clearFilters = () => {
    setFilters({
      month: "", date_from: "", date_to: "",
      center_id: "", department_id: "", employee_id: "", manager_id: "",
      request_type: "", status: "", doc_status: "",
      employment_status: "", position_id: "", professional_category_id: "",
      agreement_id: "", cotization_group_id: "", cost_center_id: "",
    });
  };

  const activeFilterCount = Object.values(filters).filter(v => v && v !== "_none").length;
  const selCls = "h-8 text-xs rounded-sm";
  const isAbsences = exportType === "absences" || exportType === "documentary";

  return (
    <div className="space-y-5" data-testid="export-page">
      <div>
        <h1 className="heading-industrial text-2xl font-bold text-slate-900 uppercase" data-testid="export-title">Exportacion Avanzada</h1>
        <p className="text-sm text-slate-500">Genera informes Excel para gestoria, administracion y control RRHH</p>
      </div>

      {/* Export Type Selector */}
      <Tabs value={exportType} onValueChange={v => { setExportType(v); setCount(null); }}>
        <TabsList className="bg-slate-100 rounded-sm h-9" data-testid="export-type-tabs">
          <TabsTrigger value="absences" className="text-[10px] uppercase tracking-wider rounded-sm data-[state=active]:bg-white gap-1.5">
            <FileSpreadsheet className="w-3.5 h-3.5" /> Ausencias / Incidencias
          </TabsTrigger>
          <TabsTrigger value="employees" className="text-[10px] uppercase tracking-wider rounded-sm data-[state=active]:bg-white gap-1.5">
            <Users className="w-3.5 h-3.5" /> Empleados Gestoria
          </TabsTrigger>
          <TabsTrigger value="documentary" className="text-[10px] uppercase tracking-wider rounded-sm data-[state=active]:bg-white gap-1.5">
            <FileWarning className="w-3.5 h-3.5" /> Documental
          </TabsTrigger>
        </TabsList>

        <TabsContent value="absences" className="mt-0">
          <p className="text-xs text-slate-500 mt-2">Vacaciones, permisos, asuntos propios, bajas medicas y cualquier ausencia registrada. Incluye datos del empleado, detalles de la solicitud, fase de aprobacion y estado documental.</p>
        </TabsContent>
        <TabsContent value="employees" className="mt-0">
          <p className="text-xs text-slate-500 mt-2">Datos laborales y administrativos completos de los empleados: datos personales, centro, departamento, puesto, jefe, convenio, SS, etc. Orientado a gestoria.</p>
        </TabsContent>
        <TabsContent value="documentary" className="mt-0">
          <p className="text-xs text-slate-500 mt-2">Solicitudes con su estado documental: documentacion pendiente, recibida o validada. Incluye quien subio, quien valido y fechas.</p>
        </TabsContent>
      </Tabs>

      <div className="grid grid-cols-4 gap-4">
        {/* Filters Panel */}
        <div className="col-span-3">
          <Card className="border-slate-200 rounded-sm">
            <CardHeader className="pb-2 pt-3 px-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xs uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                  <Filter className="w-3.5 h-3.5" /> Filtros
                  {activeFilterCount > 0 && <Badge className="text-[9px] bg-red-600 text-white ml-1">{activeFilterCount}</Badge>}
                </CardTitle>
                {activeFilterCount > 0 && (
                  <Button variant="ghost" size="sm" className="h-6 text-[10px] text-slate-500" onClick={clearFilters} data-testid="btn-clear-filters">Limpiar filtros</Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-4 pt-1">
              {/* Date filters - shown for absences & documentary */}
              {isAbsences && (
                <div className="grid grid-cols-4 gap-3 mb-3">
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase text-slate-500">Mes</Label>
                    <Input type="month" value={filters.month} onChange={e => { f("month", e.target.value); f("date_from", ""); f("date_to", ""); }} className={selCls} data-testid="filter-month" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase text-slate-500">Desde</Label>
                    <Input type="date" value={filters.date_from} onChange={e => { f("date_from", e.target.value); f("month", ""); }} className={selCls} data-testid="filter-from" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase text-slate-500">Hasta</Label>
                    <Input type="date" value={filters.date_to} onChange={e => { f("date_to", e.target.value); f("month", ""); }} className={selCls} data-testid="filter-to" />
                  </div>
                  <div />
                </div>
              )}

              {/* Common filters */}
              <div className="grid grid-cols-4 gap-3 mb-3">
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase text-slate-500">Centro</Label>
                  <Select value={filters.center_id || "_none"} onValueChange={v => f("center_id", v === "_none" ? "" : v)}>
                    <SelectTrigger className={selCls} data-testid="filter-center"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="_none">Todos</SelectItem>{centers.filter(c=>c.active).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase text-slate-500">Departamento</Label>
                  <Select value={filters.department_id || "_none"} onValueChange={v => f("department_id", v === "_none" ? "" : v)}>
                    <SelectTrigger className={selCls} data-testid="filter-dept"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="_none">Todos</SelectItem>{departments.filter(d=>d.active).map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase text-slate-500">Empleado</Label>
                  <Select value={filters.employee_id || "_none"} onValueChange={v => f("employee_id", v === "_none" ? "" : v)}>
                    <SelectTrigger className={selCls} data-testid="filter-employee"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-60"><SelectItem value="_none">Todos</SelectItem>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] uppercase text-slate-500">Jefe directo</Label>
                  <Select value={filters.manager_id || "_none"} onValueChange={v => f("manager_id", v === "_none" ? "" : v)}>
                    <SelectTrigger className={selCls} data-testid="filter-manager"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-60"><SelectItem value="_none">Todos</SelectItem>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              {/* Request-specific filters */}
              {isAbsences && (
                <div className="grid grid-cols-4 gap-3 mb-3">
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase text-slate-500">Tipo ausencia</Label>
                    <Select value={filters.request_type || "_none"} onValueChange={v => f("request_type", v === "_none" ? "" : v)}>
                      <SelectTrigger className={selCls} data-testid="filter-request-type"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="_none">Todos</SelectItem>{requestTypes.filter(t=>t.active).map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase text-slate-500">Estado solicitud</Label>
                    <Select value={filters.status || "_none"} onValueChange={v => f("status", v === "_none" ? "" : v)}>
                      <SelectTrigger className={selCls} data-testid="filter-status"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="_none">Todos</SelectItem>{leaveStates.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase text-slate-500">Estado documental</Label>
                    <Select value={filters.doc_status || "_none"} onValueChange={v => f("doc_status", v === "_none" ? "" : v)}>
                      <SelectTrigger className={selCls} data-testid="filter-doc-status"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="_none">Todos</SelectItem><SelectItem value="pending">Pendiente</SelectItem><SelectItem value="received">Recibida</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div />
                </div>
              )}

              {/* Employee-specific filters */}
              {exportType === "employees" && (
                <div className="grid grid-cols-4 gap-3 mb-3">
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase text-slate-500">Situacion laboral</Label>
                    <Select value={filters.employment_status || "_none"} onValueChange={v => f("employment_status", v === "_none" ? "" : v)}>
                      <SelectTrigger className={selCls} data-testid="filter-emp-status"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="_none">Todas</SelectItem>{Object.entries(STATUS_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase text-slate-500">Puesto</Label>
                    <Select value={filters.position_id || "_none"} onValueChange={v => f("position_id", v === "_none" ? "" : v)}>
                      <SelectTrigger className={selCls}><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="_none">Todos</SelectItem>{positions.filter(p=>p.active).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase text-slate-500">Categoria profesional</Label>
                    <Select value={filters.professional_category_id || "_none"} onValueChange={v => f("professional_category_id", v === "_none" ? "" : v)}>
                      <SelectTrigger className={selCls}><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="_none">Todas</SelectItem>{profCategories.filter(c=>c.active).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase text-slate-500">Convenio</Label>
                    <Select value={filters.agreement_id || "_none"} onValueChange={v => f("agreement_id", v === "_none" ? "" : v)}>
                      <SelectTrigger className={selCls}><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="_none">Todos</SelectItem>{agreements.filter(a=>a.active).map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              {exportType === "employees" && (
                <div className="grid grid-cols-4 gap-3">
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase text-slate-500">Grupo cotizacion</Label>
                    <Select value={filters.cotization_group_id || "_none"} onValueChange={v => f("cotization_group_id", v === "_none" ? "" : v)}>
                      <SelectTrigger className={selCls}><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="_none">Todos</SelectItem>{contribGroups.filter(g=>g.active).map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase text-slate-500">Centro de coste</Label>
                    <Select value={filters.cost_center_id || "_none"} onValueChange={v => f("cost_center_id", v === "_none" ? "" : v)}>
                      <SelectTrigger className={selCls}><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="_none">Todos</SelectItem>{costCenters.filter(c=>c.active).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Export Action Panel */}
        <div className="col-span-1">
          <Card className="border-slate-200 rounded-sm sticky top-4">
            <CardHeader className="pb-2 pt-3 px-4">
              <CardTitle className="text-xs uppercase tracking-wider text-slate-700">Generar</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 pt-1 space-y-3">
              <div className="text-center py-3 bg-slate-50 rounded-sm">
                {countLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mx-auto text-slate-400" />
                ) : (
                  <>
                    <p className="text-3xl font-bold text-slate-900" data-testid="export-count">{count ?? "-"}</p>
                    <p className="text-[10px] uppercase text-slate-400 mt-0.5">registros</p>
                  </>
                )}
              </div>

              {activeFilterCount > 0 && (
                <div className="space-y-1">
                  <p className="text-[10px] uppercase text-slate-400">Filtros activos</p>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(filters).filter(([, v]) => v && v !== "_none").map(([k]) => (
                      <Badge key={k} variant="outline" className="text-[8px] rounded-sm">{k.replace(/_/g, " ")}</Badge>
                    ))}
                  </div>
                </div>
              )}

              <Button
                className="w-full bg-green-700 hover:bg-green-800 text-white text-xs rounded-sm h-9"
                onClick={doExport}
                disabled={loading || count === 0}
                data-testid="btn-export"
              >
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
                {loading ? "Generando..." : "Exportar Excel"}
              </Button>

              <p className="text-[9px] text-slate-400 text-center">
                {exportType === "absences" && "rrhh_ausencias_YYYY-MM.xlsx"}
                {exportType === "employees" && "rrhh_gestoria_centro_YYYY_MM.xlsx"}
                {exportType === "documentary" && "rrhh_documental_YYYY-MM.xlsx"}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
